import { Component } from '@theme/component'
import { ThemeEvents } from '@theme/events'

// Constants for nutrition data keys
const NUTRITION_KEYS = {
  NUTRITION: 'nutrition',
  NUTRITION_PORTION: 'nutrition_portion'
}

/**
 * A custom element that manages product nutrition display with portion selection.
 *
 * @typedef {object} ProductNutritionRefs
 * @property {HTMLSelectElement} portionSelect - The portion selector dropdown.
 * @property {HTMLSelectElement} tasteSelect - The taste selector dropdown.
 * @property {HTMLTableElement} nutritionTable - The nutrition table element.
 * @extends Component<ProductNutritionRefs>
 */
export class ProductNutritionComponent extends Component {
  /** @type {string} */
  #currentPortion = NUTRITION_KEYS.NUTRITION

  /** @type {string} */
  #currentTaste = ''

  connectedCallback() {
    super.connectedCallback()

    const tasteSelect = this.querySelector('select[name="properties[taste]"]')
    if (tasteSelect instanceof HTMLSelectElement) {
      this.#currentTaste = tasteSelect.value
    }

    this.#setupEventListeners()
    this.#updateNutritionDisplay()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.#removeEventListeners()
  }

  /**
   * Sets up event listeners for the portion selector.
   */
  #setupEventListeners() {
    const portionSelects = this.querySelectorAll('select[name="properties[portion]"]')


    if (portionSelects) {
      portionSelects.forEach(portionSelect => {
        portionSelect.addEventListener('change', this.#handlePortionChange)
      })
    }

    const tasteSelect = this.querySelector('select[name="properties[taste]"]')
    if (tasteSelect) {
      tasteSelect.addEventListener('change', this.#handleTasteChange)
    }
  }

  /**
   * Removes event listeners.
   */
  #removeEventListeners() {
    const portionSelects = this.querySelectorAll('select[name="properties[portion]"]')

    if (portionSelects) {
      portionSelects.forEach(portionSelect => {
        portionSelect.removeEventListener('change', this.#handlePortionChange)
      })
    }

    const tasteSelect = this.querySelector('select[name="properties[taste]"]')
    if (tasteSelect) {
      tasteSelect.removeEventListener('change', this.#handleTasteChange)
    }
  }

  /**
   * Handles the taste selection change event.
   * @param {Event} event - The change event.
   */
  #handleTasteChange = (event) => {
    const tasteSelect = /** @type {HTMLSelectElement} */(event.target)
    if (!tasteSelect) return

    this.#currentTaste = tasteSelect.value

    this.#updateNutritionDisplay()
    this.#updatePortionSelectsVisibility()
  }


  /**
   * Updates the portion selects visibility based on the selected taste.
   */
  #updatePortionSelectsVisibility() {
    this.querySelectorAll('.portion-selector').forEach(select => {
      if (!(select instanceof HTMLElement)) {
        return
      }

      if (select.id.includes(this.#currentTaste)) {
        select.style.display = 'block'
      } else {
        select.style.display = 'none'
      }

    })
  }

  /**
   * Handles the portion selection change event.
   * @param {Event} event - The change event.
   */
  #handlePortionChange = (event) => {
    const portionSelect = /** @type {HTMLSelectElement} */(event.target)
    if (!portionSelect) return

    this.#currentPortion = portionSelect.value
    this.#updateNutritionDisplay()

    this.querySelectorAll('select[name="properties[portion]"]').forEach(select => {
      if (select instanceof HTMLSelectElement && select !== portionSelect) {
        select.value = this.#currentPortion
      }
    })
  }

  /**
   * Updates the nutrition table display based on the selected portion.
   */
  #updateNutritionDisplay() {
    const nutritionTables = this.querySelectorAll('table')
    if (!nutritionTables) return

    this.#showSelectedColumn()
  }

  /**
   * Shows the selected column and hides the other.
   */
  #showSelectedColumn() {
    const nutritionTables = this.querySelectorAll('table')
    if (!nutritionTables) return

    let activeTable = nutritionTables[0]

    // Check if we are a at a multi-flavor product
    if (nutritionTables.length > 1 && this.#currentTaste) {
      for (const table of nutritionTables) {
        const tasteOptionId = table.getAttribute('data-taste-option-id')

        if (tasteOptionId === this.#currentTaste) {
          activeTable = table
          table.style.display = 'table'
        } else {
          table.style.display = 'none'
        }
      }
    }

    if (!activeTable) return

    const nutritionColumns = activeTable.querySelectorAll('.nutrition-column')
    const nutritionPortionColumns = activeTable.querySelectorAll('.nutrition-portion-column')

    if (this.#currentPortion === NUTRITION_KEYS.NUTRITION) {
      // Show nutrition columns, hide nutrition_portion columns
      nutritionColumns.forEach(col => /** @type {HTMLElement} */(col).style.display = 'table-cell')
      nutritionPortionColumns.forEach(col => /** @type {HTMLElement} */(col).style.display = 'none')
    } else {
      // Show nutrition_portion columns, hide nutrition columns
      nutritionColumns.forEach(col => /** @type {HTMLElement} */(col).style.display = 'none')
      nutritionPortionColumns.forEach(col => /** @type {HTMLElement} */(col).style.display = 'table-cell')
    }
  }
}

// Register the custom element
customElements.define('product-nutrition', ProductNutritionComponent) 